import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, Trophy, Users, History, LayoutGrid, Newspaper, CalendarClock, Handshake } from "lucide-react";
import { Crest } from "@/components/Crest";
import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { SectionTitle } from "@/components/SectionTitle";
import { IMAGES } from "@/lib/images";
import { useSeo } from "@/lib/useSeo";
import { listNews, listMatches, listResults, listPartners, getStats } from "@/lib/db";

export default function Home() {
  const navigate = useNavigate();
  useSeo({ title: "Αρχική", description: "Καλωσορίσατε στην επίσημη ιστοσελίδα της Ρεάλ Δυτικής Αττικής — ποδόσφαιρο & μπάσκετ, νέα, επόμενοι αγώνες, αποτελέσματα και συνεργάτες." });
  const [news, setNews] = useState([]);
  const [matches, setMatches] = useState([]);
  const [results, setResults] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({ years: 0, athletes: 0, titles: 0, departments: 0 });

  useEffect(() => {
    listNews().then((d) => setNews(d.slice(0, 3))).catch(() => {});
    listMatches().then((d) => setMatches(d.slice(0, 6))).catch(() => {});
    listResults().then((d) => setResults(d)).catch(() => {});
    listPartners().then((d) => setPartners(d)).catch(() => {});
    getStats().then((d) => setStats(d)).catch(() => {});
  }, []);

  const statItems = [
    { icon: History, value: stats.years, label: "Έτη Ιστορίας", suffix: "" },
    { icon: Users, value: stats.athletes, label: "Αθλητές", suffix: "+" },
    { icon: Trophy, value: stats.titles, label: "Τίτλοι", suffix: "" },
    { icon: LayoutGrid, value: stats.departments, label: "Τμήματα", suffix: "" },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/85 to-royal/70" />
        <div className="absolute inset-0 bg-navy/40" />

        <div className="club-container relative z-10 flex flex-col pt-24 lg:items-center lg:text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Crest size={120} className="drop-shadow-2xl" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-heading mt-6 max-w-4xl text-5xl font-900 uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl"
          >
            Ρεάλ Δυτικής<br />
            <span className="text-gold">Αττικής</span>
          </motion.h1>

          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "9rem" }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 h-1.5 rounded bg-gold"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 max-w-xl text-lg text-ice/90 sm:text-xl"
          >
            Πάθος, ήθος και ομαδικότητα. Ένας σύλλογος, δύο αθλήματα — Ποδόσφαιρο & Μπάσκετ στην καρδιά της Δυτικής Αττικής.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-9 flex flex-wrap gap-4 lg:justify-center"
          >
            <Link
              to="/roster"
              data-testid="hero-roster-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-lg font-700 uppercase tracking-wide text-white transition-all duration-200 hover:bg-brand/80 hover:shadow-[0_0_30px_rgba(60,115,192,0.7)]"
            >
              Δες το Ρόστερ
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-8 py-4 font-display text-lg font-700 uppercase tracking-wide text-gold transition-all duration-200 hover:bg-gold hover:text-navy"
            >
              Επικοινωνία
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SPLIT SHOWCASE */}
      <section className="flex min-h-[520px] flex-col md:flex-row" data-testid="split-showcase">
        {[
          { key: "football", title: "Ποδόσφαιρο", img: IMAGES.football, sub: "Το ιστορικό μας τμήμα" },
          { key: "basketball", title: "Μπάσκετ", img: IMAGES.basketball, sub: "Δύναμη στο παρκέ" },
        ].map((dep) => (
          <motion.button
            key={dep.key}
            onClick={() => navigate(`/roster?tab=${dep.key}`)}
            data-testid={`showcase-${dep.key}`}
            className="group relative flex-1 overflow-hidden text-left transition-all duration-500 md:hover:flex-[1.35]"
          >
            <div
              className="absolute inset-0 bg-cover bg-center brightness-[0.55] transition-all duration-500 group-hover:scale-110 group-hover:brightness-90"
              style={{ backgroundImage: `url(${dep.img})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
            <div className="relative z-10 flex h-full min-h-[280px] flex-col justify-end p-8 sm:p-12">
              <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-gold">
                {dep.sub}
              </p>
              <h3 className="font-heading mt-2 text-5xl font-900 uppercase tracking-tight text-white sm:text-6xl">
                {dep.title}
              </h3>
              <span className="mt-4 inline-flex items-center gap-2 font-display text-lg font-700 uppercase tracking-wide text-ice opacity-0 transition-all duration-300 group-hover:opacity-100">
                Δες το ρόστερ <ArrowRight size={18} />
              </span>
            </div>
          </motion.button>
        ))}
      </section>

      {/* CLUB IN NUMBERS */}
      <section className="bg-royal/30 py-20 sm:py-28">
        <div className="club-container">
          <Reveal className="mb-14 text-center">
            <SectionTitle
              kicker="Ο σύλλογος σε αριθμούς"
              title="Η Δύναμή μας"
              className="inline-block text-center"
            />
          </Reveal>
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {statItems.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-navy/60 p-8 text-center transition-all duration-300 hover:border-gold/50 hover:shadow-[0_16px_40px_-16px_rgba(60,115,192,0.6)]">
                  <s.icon className="mx-auto text-brand" size={34} />
                  <p className="font-display mt-4 text-5xl font-900 text-gold sm:text-6xl">
                    <Counter to={s.value} suffix={s.suffix} />
                  </p>
                  <p className="mt-2 font-display text-sm font-600 uppercase tracking-widest text-ice">
                    {s.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="py-20 sm:py-28">
        <div className="club-container">
          <Reveal className="mb-14">
            <SectionTitle kicker="Ενημέρωση" title="Τελευταία Νέα" />
          </Reveal>
          {news.length > 0 ? (
            <div className="grid gap-7 md:grid-cols-3">
              {news.map((n, i) => (
                <Reveal key={n.id} delay={i * 0.08}>
                  <Link
                    to={`/news/${n.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-royal/40 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_45px_-18px_rgba(60,115,192,0.7)]"
                    data-testid={`news-card-${n.id}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-navy">
                      {n.image && (
                        <img
                          src={n.image}
                          alt={n.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="font-display text-xs font-600 uppercase tracking-widest text-gold">
                        {formatDate(n.date)}
                      </span>
                      <h3 className="font-heading mt-2 text-2xl font-800 leading-tight tracking-tight text-white">
                        {n.title}
                      </h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-ice/80">
                        {n.excerpt}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1 font-display text-sm font-700 uppercase tracking-wide text-brand transition-colors group-hover:text-gold">
                        Διαβάστε περισσότερα <ArrowRight size={15} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <EmptyState icon={Newspaper} title="Δεν υπάρχουν νέα ακόμη" subtitle="Σύντομα θα δημοσιεύσουμε τα τελευταία νέα και τις ανακοινώσεις του συλλόγου." />
          )}
        </div>
      </section>

      {/* NEXT MATCHES STRIP */}
      <section className="bg-gradient-to-r from-royal to-navy py-16">
        <div className="club-container">
          <Reveal className="mb-8">
            <SectionTitle kicker="Πρόγραμμα" title="Επόμενοι Αγώνες" />
          </Reveal>
          {matches.length > 0 ? (
            <div className="flex gap-5 overflow-x-auto pb-4 no-scrollbar" data-testid="matches-strip">
              {matches.map((m, i) => (
                <Reveal key={m.id} delay={i * 0.05}>
                  <div className="min-w-[260px] rounded-xl border border-white/10 bg-navy/70 p-6">
                    <div className="flex items-center justify-between">
                      <span
                        className={`rounded-full px-3 py-1 font-display text-xs font-700 uppercase tracking-wide ${
                          m.sport === "football" ? "bg-brand/20 text-brand" : "bg-gold/20 text-gold"
                        }`}
                      >
                        {m.sport === "football" ? "Ποδόσφαιρο" : "Μπάσκετ"}
                      </span>
                      <span className="text-xs font-600 uppercase text-ice/60">
                        {m.home ? "Εντός" : "Εκτός"}
                      </span>
                    </div>
                    <h4 className="font-heading mt-4 text-2xl font-800 text-white">
                      vs {m.opponent}
                    </h4>
                    <div className="mt-4 space-y-2 text-sm text-ice/80">
                      <p className="flex items-center gap-2">
                        <Calendar size={15} className="text-gold" /> {formatDate(m.date)}
                      </p>
                      <p className="flex items-center gap-2">
                        <MapPin size={15} className="text-gold" /> {m.venue}
                      </p>
                      {m.competition && (
                        <p className="flex items-center gap-2">
                          <Trophy size={15} className="text-gold" /> {m.competition}
                        </p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <EmptyState icon={CalendarClock} title="Δεν υπάρχουν προγραμματισμένοι αγώνες" subtitle="Το πρόγραμμα των επόμενων αγώνων θα ανακοινωθεί σύντομα. Μείνετε συντονισμένοι!" />
          )}
        </div>
      </section>

      {/* RECENT RESULTS */}
      <section className="py-16">
        <div className="club-container">
          <Reveal className="mb-8">
            <SectionTitle kicker="Πρόσφατα" title="Αποτελέσματα" />
          </Reveal>
          {results.length > 0 ? (
            Object.entries(
              results.reduce((acc, r) => {
                const key = r.competition || "Άλλες διοργανώσεις";
                (acc[key] = acc[key] || []).push(r);
                return acc;
              }, {})
            ).map(([comp, list]) => (
              <div key={comp} className="mb-10">
                <h3 className="mb-4 flex items-center gap-3 font-display text-xl font-800 uppercase tracking-wide text-gold">
                  <Trophy size={18} /> {comp}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {list.map((r, i) => {
                    const win = r.our_score > r.opp_score;
                    const draw = r.our_score === r.opp_score;
                    return (
                      <Reveal key={r.id} delay={i * 0.06}>
                        <div className="rounded-xl border border-white/10 bg-royal/30 p-5" data-testid={`result-card-${r.id}`}>
                          <div className="flex items-center justify-between">
                            <span className={`rounded-full px-3 py-1 font-display text-xs font-700 uppercase tracking-wide ${r.sport === "football" ? "bg-brand/20 text-brand" : "bg-gold/20 text-gold"}`}>
                              {r.sport === "football" ? "Ποδόσφαιρο" : "Μπάσκετ"}
                            </span>
                            <span className={`font-display text-xs font-700 uppercase ${win ? "text-green-400" : draw ? "text-ice/70" : "text-red-400"}`}>
                              {win ? "Νίκη" : draw ? "Ισοπαλία" : "Ήττα"}
                            </span>
                          </div>
                          <p className="mt-3 text-sm text-ice/70">{r.home ? "Εντός έδρας" : "Εκτός έδρας"} · {formatDate(r.date)}</p>
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <span className="font-display text-lg font-800 uppercase leading-tight text-white">vs {r.opponent}</span>
                            <span className="font-display text-2xl font-900 text-gold">{r.our_score}-{r.opp_score}</span>
                          </div>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <EmptyState icon={Trophy} title="Δεν υπάρχουν αποτελέσματα ακόμη" subtitle="Μόλις ολοκληρωθούν οι πρώτοι αγώνες, τα αποτελέσματα θα εμφανιστούν εδώ." />
          )}
        </div>
      </section>

      {/* PARTNERS */}
      <section className="border-t border-white/10 py-16">
        <div className="club-container mb-8">
          <SectionTitle kicker="Δίπλα μας" title="Οι Συνεργάτες μας" />
        </div>
        <div className="relative overflow-hidden">
          {partners.length === 0 ? (
            <div className="club-container">
              <EmptyState icon={Handshake} title="Σύντομα κοντά μας" subtitle="Οι συνεργάτες & χορηγοί που στηρίζουν τον σύλλογο θα παρουσιάζονται εδώ." />
            </div>
          ) : partners.length >= 5 ? (
            <div className="marquee gap-6">
              {[...partners, ...partners].map((p, i) => (
                <PartnerCard key={`${p.id}-${i}`} p={p} testid={`partner-marquee-${i}`} />
              ))}
            </div>
          ) : (
            <div className="club-container flex flex-wrap items-center justify-center gap-6">
              {partners.map((p) => (
                <PartnerCard key={p.id} p={p} testid={`partner-${p.id}`} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-royal/20 px-8 py-16 text-center"
      data-testid="empty-state"
    >
      <div className="grid h-16 w-16 place-items-center rounded-full bg-brand/15 ring-1 ring-brand/30">
        <Icon size={30} className="text-gold" />
      </div>
      <h4 className="font-heading mt-5 text-xl font-800 tracking-tight text-white">{title}</h4>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-ice/60">{subtitle}</p>
    </div>
  );
}

function PartnerCard({ p, testid }) {
  return (
    <Link
      to="/partners"
      className="flex h-24 min-w-[200px] items-center justify-center rounded-xl border border-white/10 bg-royal/40 px-8 transition-colors hover:border-gold/60"
      data-testid={testid}
    >
      {p.logo ? (
        <img src={p.logo} alt={p.name} className="max-h-12 object-contain" />
      ) : (
        <span className="font-display text-xl font-700 uppercase tracking-wide text-ice">
          {p.name}
        </span>
      )}
    </Link>
  );
}

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("el-GR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return d;
  }
}
